import React from 'react';
import {
  Badge,
  Card,
  CardSection,
  CondensedLabel,
  CondensedMedium,
  DateUtil,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  LayoutItem,
  Paragraph,
  SectionHeader,
  Tooltip,
} from '@axiom/ui';
import { CandidateStatusLabelsMap, PositionsConst } from '@axiom/const';
import type { OpportunityCandidate } from '@axiom/validation';

import { CandidateUtil } from '../../utils/candidate-util';

import { TalentOpportunityCardContextMenu } from './TalentOpportunityCardContextMenu';
import { TalentOpportunityCardUtil } from './TalentOpportunityCardUtil';
import type { TalentOpportunityCardType } from './TalentOpportunityCardUtil';

export type { TalentOpportunityCardType } from './TalentOpportunityCardUtil';
export { TalentOpportunityCardTypeNames } from './TalentOpportunityCardUtil';
export const { talentOpportunityCardTypeNameFormatter } =
  TalentOpportunityCardUtil;

const { EndDateStatuses } = PositionsConst;
const { DASH } = TalentOpportunityCardUtil;

export const TalentOpportunityCard = ({
  type,
  opportunityCandidate,
}: {
  type: TalentOpportunityCardType;
  opportunityCandidate: OpportunityCandidate;
}) => {
  const talentOnPosition = TalentOpportunityCardUtil.isConsiderationCard(type)
    ? TalentOpportunityCardUtil.buildTalentOnPosition(opportunityCandidate)
    : '';

  return (
    <Card name={`${opportunityCandidate.id}-card`}>
      <CardSection>
        <Grid columns={2}>
          <GridRow>
            <GridColumn>
              <SectionHeader name="jobname-header" maxLines={1}>
                {opportunityCandidate.jobName}
              </SectionHeader>
            </GridColumn>
            <GridColumn>
              <Layout position="right">
                <LayoutItem>
                  <Badge
                    background={CandidateUtil.getCandidateStatusBadgeColor(
                      opportunityCandidate.candidateStatus
                    )}
                    name="candidateStatusBadge"
                  >
                    <CondensedMedium>
                      {opportunityCandidate.candidateStatus
                        ? CandidateStatusLabelsMap.get(
                            opportunityCandidate.candidateStatus
                          )
                        : '-'}
                    </CondensedMedium>
                  </Badge>
                </LayoutItem>
                <LayoutItem position="center">
                  <TalentOpportunityCardContextMenu
                    opportunityCandidate={opportunityCandidate}
                  />
                </LayoutItem>
              </Layout>
            </GridColumn>
          </GridRow>
        </Grid>
        <Gutter vertical="8px" />
        <Grid columns={2}>
          <GridRow>
            <GridColumn>
              <Layout direction="vertical">
                {TalentOpportunityCardUtil.isConsiderationCard(type) && (
                  <Paragraph name="created-field">
                    <CondensedLabel>Created: </CondensedLabel>
                    <CondensedMedium as="span">
                      {TalentOpportunityCardUtil.formatCreateOrUpdateDate(
                        opportunityCandidate.createdAt,
                        true
                      )}
                    </CondensedMedium>
                  </Paragraph>
                )}
                <Paragraph name="billing-field">
                  <CondensedLabel>Billing: </CondensedLabel>
                  <CondensedMedium as="span">
                    {TalentOpportunityCardUtil.formatBilling(
                      opportunityCandidate.position
                    )}
                  </CondensedMedium>
                </Paragraph>
                <Paragraph name="account-field">
                  <CondensedLabel>Account: </CondensedLabel>
                  <CondensedMedium as="span">
                    {opportunityCandidate.accountName || DASH}
                  </CondensedMedium>
                </Paragraph>
                <Paragraph name="ts-lead-field">
                  <CondensedLabel>TS lead: </CondensedLabel>
                  <CondensedMedium as="span">
                    {opportunityCandidate.ownerUser?.fullName || DASH}
                  </CondensedMedium>
                </Paragraph>
                {TalentOpportunityCardUtil.isConsiderationCard(type) && (
                  <Paragraph name="opportunity-owner-field">
                    <CondensedLabel>Opportunity owner: </CondensedLabel>
                    <CondensedMedium as="span">
                      {opportunityCandidate.salesLead?.fullName || DASH}
                    </CondensedMedium>
                  </Paragraph>
                )}
                {TalentOpportunityCardUtil.isEngagementCard(type) && (
                  <Paragraph name="account-owner-field">
                    <CondensedLabel>Account owner: </CondensedLabel>
                    <CondensedMedium as="span">
                      {opportunityCandidate.account?.ownerUser?.fullName ||
                        DASH}
                    </CondensedMedium>
                  </Paragraph>
                )}
              </Layout>
            </GridColumn>
            <GridColumn>
              <Layout direction="vertical">
                {TalentOpportunityCardUtil.isConsiderationCard(type) && (
                  <>
                    <Paragraph name="changed-status-field">
                      <CondensedLabel>Changed status: </CondensedLabel>
                      <CondensedMedium as="span">
                        {TalentOpportunityCardUtil.formatChangedStatus(
                          opportunityCandidate
                        )}
                      </CondensedMedium>
                    </Paragraph>
                    <Paragraph name="talent-on-position-field">
                      <CondensedLabel>
                        Other talent on position:{' '}
                      </CondensedLabel>
                      <CondensedMedium
                        name="talent-on-position-value"
                        as="span"
                      >
                        {talentOnPosition || DASH}
                      </CondensedMedium>
                    </Paragraph>
                    <Paragraph name="last-updated-field">
                      <CondensedLabel>Last updated: </CondensedLabel>
                      <CondensedMedium as="span">
                        {TalentOpportunityCardUtil.formatCreateOrUpdateDate(
                          opportunityCandidate.updatedAt,
                          false
                        )}
                      </CondensedMedium>
                    </Paragraph>
                  </>
                )}
                {TalentOpportunityCardUtil.isEngagementCard(type) && (
                  <>
                    <Paragraph name="start-date-field">
                      <CondensedLabel>Start date: </CondensedLabel>
                      <CondensedMedium as="span">
                        {DateUtil.displayDayAndDate(
                          opportunityCandidate.position?.startDate
                        ) || DASH}
                      </CondensedMedium>
                    </Paragraph>
                    <Paragraph name="roll-off-field">
                      <CondensedLabel>Roll-off: </CondensedLabel>
                      <CondensedMedium as="span">
                        {opportunityCandidate.position?.endDateStatus ===
                        EndDateStatuses.Confirmed ? (
                          <Badge name="roll-off-badge" background="yellow">
                            {EndDateStatuses.Confirmed}
                          </Badge>
                        ) : (
                          EndDateStatuses.NotConfirmed
                        )}{' '}
                        {DateUtil.displayDayAndDate(
                          opportunityCandidate.position?.endDate
                        )}
                      </CondensedMedium>
                    </Paragraph>
                    <Paragraph>
                      <CondensedLabel>Original compensation:</CondensedLabel>
                      <Tooltip name="original-compensation-tooltip">
                        Original compensation is set when the opportunity is
                        created and may not reflect subsequent rate changes or
                        corrections.
                      </Tooltip>
                      <CondensedMedium
                        as="span"
                        name="original-compensation-field"
                      >
                        {TalentOpportunityCardUtil.formatCompensation(
                          opportunityCandidate
                        )}
                      </CondensedMedium>
                    </Paragraph>
                    <Paragraph name="id-field">
                      <CondensedLabel>ID: </CondensedLabel>
                      <CondensedMedium as="span">
                        {opportunityCandidate.integrationId}
                      </CondensedMedium>
                    </Paragraph>
                  </>
                )}
              </Layout>
            </GridColumn>
          </GridRow>
        </Grid>
      </CardSection>
    </Card>
  );
};
